import React, { useEffect } from 'react';

import { AdornedInput, Adornment, FormField, FormHelp, Label } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models';
import { RENT_ADJUSTMENT } from '../../../constants';
import YesAndNo from '../../../../shared/components/Widgets/YesAndNo';
import { CURRENCY_INPUT_PLACEHOLDER, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function RentAdjustment({ section, setSectionValues }: Props) {
  useEffect(() => {
    section.setCustomFieldValidator(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR, isAmountValid);
  }, []);

  function isAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > RENT_ADJUSTMENT.BOUNDS.MAX_AMOUNT) {
      section
        .getField(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(RENT_ADJUSTMENT.BOUNDS.MAX_AMOUNT));
      return false;
    }
    return true;
  }

  function onNoInitialRentWithoutLimitations(): void {
    setSectionValues({
      [RENT_ADJUSTMENT.FIELD_NAME.INITIAL_RENT_WITHOUT_LIMITATIONS]: false,
    });
  }

  function onYesInitialRentWithoutLimitations(): void {
    setSectionValues({
      [RENT_ADJUSTMENT.FIELD_NAME.INITIAL_RENT_WITHOUT_LIMITATIONS]: true,
      [RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR]: null,
    });
  }

  function onRentInEffectPriorChange(event: any): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR]: value,
    });
  }

  const initialRentWithoutLimitationsEnabled = section.getFieldValue(
    RENT_ADJUSTMENT.FIELD_NAME.INITIAL_RENT_WITHOUT_LIMITATIONS,
  );

  return (
    <>
      <FormField
        label={
          <Label size="lg" marginBottom="xs">
            Is the owner permitted to set the initial rent on this unit without limitations?
          </Label>
        }
        control={
          <>
            <YesAndNo
              yesHandler={onYesInitialRentWithoutLimitations}
              noHandler={onNoInitialRentWithoutLimitations}
              yesActive={initialRentWithoutLimitationsEnabled}
            />
            {initialRentWithoutLimitationsEnabled === false && (
              <FormField
                marginTop="sm"
                label={<Label>What was the rent in effect when the prior tenant vacated?</Label>}
                control={
                  <AdornedInput
                    leftAdornment={<Adornment>$</Adornment>}
                    input={
                      <CurrencyInput
                        placeholder={CURRENCY_INPUT_PLACEHOLDER}
                        value={section.getFieldValue(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR)}
                        onChange={onRentInEffectPriorChange}
                        onBlur={onRentInEffectPriorChange}
                      />
                    }
                    rightAdornment={<Adornment>per month</Adornment>}
                  />
                }
                description={
                  section.getField(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR).hasError && (
                    <FormHelp>
                      {section.getField(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR).getErrorMessage()}
                    </FormHelp>
                  )
                }
                error={section.getField(RENT_ADJUSTMENT.FIELD_NAME.RENT_IN_EFFECT_PRIOR).hasError}
              />
            )}
          </>
        }
      />
    </>
  );
}

RentAdjustment.description = 'Oakland specific rent adjustment screen';

export default RentAdjustment;
